<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    :class="{
      sidebar: true,
      '--short': !fullView,
      // '--hide': pagesTransition,
      ['--for-' + routeName]: true,
    }"
  >
    <div class="sidebar__logo">
      <router-link to="/">E<span class="_tail">ditor</span></router-link>
      <span class="_change-view" @click="toggleFullView()">{{
        fullView ? "-" : "+"
      }}</span>
      <div v-if="params?.devMode" style="font-size: 18px">DEVMODE</div>
    </div>
    <div v-if="isAuthenticated" class="sidebar__menu">
      <div
        v-for="(menuItem, num) in menuItemsPermitted"
        :key="num"
        :class="{
          sidebar__menu__item: true,
          '--active': menuItemInUrl(menuItem) || menuItem.active,
        }"
        @mouseover="showSubItems(menuItem)"
        @mouseout="hideSubItems(menuItem)"
      >
        <router-link v-if="!menuItem.subItems.length" :to="'/' + menuItem.url">
          {{ menuItem.title }}
        </router-link>
        <div v-else>
          {{ menuItem.title }}
          <span class="_plus" @click="showSubItems(menuItem)">+</span>
        </div>

        <div v-if="menuItem.subItems.length" class="sidebar__menu__subitems">
          <div
            v-for="(subItem, subItemIndex) in permittedPacks(
              menuItem.subItems,
              menuItem
            )"
            :key="subItemIndex"
            :class="{ '--active': subMenuItemInUrl(subItem) }"
          >
            <router-link
              v-if="subItem.url"
              :to="'/' + menuItem.url + '/' + subItem.url"
              class="_item"
            >
              {{ subItem.title }}
            </router-link>
            <div v-else class="_item">
              {{ subItem.title }}
              <div
                v-if="
                  subItem.moreItems != undefined && subItem.moreItems.length
                "
                class="sidebar__menu__subitems__moreitems"
              >
                <router-link
                  v-for="(m, mIndex) in permittedModules(subItem.moreItems)"
                  :key="mIndex"
                  :to="'/' + menuItem.url + '/' + m.url"
                  class="_final-item"
                >
                  {{ m.title }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isAuthenticated" class="sidebar__hotmenu">
      <ul class="no-style">
        <li v-if="isModulePermitted('lp')">
          <router-link :to="'/modules/' + MODULE_LP">LP</router-link>
        </li>
        <li v-if="isModulePermitted('cd')">
          <router-link :to="'/modules/' + MODULE_CD">CD</router-link>
        </li>
        <li v-if="isModulePermitted('acc_products')">
          <router-link :to="'/modules/' + MODULE_ACC">АК</router-link>
        </li>
      </ul>
    </div>
    <div v-if="isAuthenticated" class="sidebar__logout">
      <span @click="logoutFromSidebar()" title="Выйти">
        <span class="_text">Выйти</span>
        <svg
          class="_icon"
          fill="none"
          height="24"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17 16L21 12M21 12L17 8M21 12L7 12M13 16V17C13 18.6569 11.6569 20 10 20H6C4.34315 20 3 18.6569 3 17V7C3 5.34315 4.34315 4 6 4H10C11.6569 4 13 5.34315 13 7V8"
            stroke="#fff"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </span>
    </div>
  </div>
</template>

<script setup>
// LLM:METADATA
// :topology: [Domain:UI | Role:Component | Pattern:Navigation]
// :relates-to: uses: "useUserPermissions", uses: "useAuth", "vue-router", required-by: "App.vue"
// :rationale: "Provides main navigation menu with permission-based filtering and central logout functionality."
// :contract: pre: "Requires authenticated user context", post: "Renders menu items based on permissions and funnels logout through the shared auth cleanup path."
// :references: PRD: "Navigation", SPEC: "SPEC-NAV-001"
// :decision_cache: "Chose script setup for better composition API usage [SIDEBAR-001]"
// LLM:END

import { ref, computed, watch, defineEmits } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { useAuth } from "@/composables/useAuth"
import { useUserPermissions } from "@/composables/useUserPermissions"
import { MODULE_LP, MODULE_CD, MODULE_ACC } from "@/constants/modules"
import { EDITOR_MENU_TOPOLOGY } from "@/constants/editorMenuTopology"

const store = useStore()
const route = useRoute()
const { logout } = useAuth()
const {
  permissions,
  isModulePermitted,
  isBlockPermitted,
  isPackPermitted,
  isServiceItemPermittedForUser,
  isReportPermitted,
  canManageAccess,
} = useUserPermissions()

const emit = defineEmits(["viewchange"])

// State
const fullView = ref(true)
const menuTopology = EDITOR_MENU_TOPOLOGY
const menuItems = ref(
  menuTopology
    .filter((item) => item.menu !== false)
    .map((item) => ({
      ...item,
      active: false,
      subItems: (item.subItems || []).map((subItem) => ({ ...subItem })),
    }))
)

defineExpose({ menuTopology })

// Computed
const isAuthenticated = computed(() => store.getters.getUserRole() != "")
const params = computed(() => store.getters.getParams())
const routeName = computed(() => route.name)
const modules = computed(() => store.getters.getMenuModules())

// Methods
const toggleFullView = () => {
  fullView.value = !fullView.value
  emit("viewchange")
}

// Sync modules to menu
watch(
  modules,
  (newVal) => {
    // Guard against undefined newVal - use empty array as fallback
    menuItems.value.find(
      (item) => item.childrenOwner === "store.menuModules"
    ).subItems = newVal || []
  },
  { immediate: true }
)

const menuItemInUrl = (menuItem) => {
  return route.path.indexOf("/" + menuItem.url) !== -1
}

const subMenuItemInUrl = (menuItem) => {
  return menuItem.url && route.path.indexOf("/" + menuItem.url) !== -1
}

const showSubItems = (menuItem) => {
  menuItems.value.forEach((item) => {
    item.active = false
  })
  menuItem.active = true
}

const hideSubItems = (menuItem) => {
  if (routeName.value != menuItem.url) menuItem.active = false
}

// Permissions
const menuItemsPermitted = computed(() => {
  return menuItems.value.filter((item) => {
    if (item.visibility === "access.manage") {
      return canManageAccess.value
    }
    if (item.visibility === "modules") {
      const permittedPackIds = permissions.value?.packs
      return (
        isBlockPermitted(item.url) ||
        (Array.isArray(permittedPackIds) && permittedPackIds.length > 0)
      )
    }
    return isBlockPermitted(item.url)
  })
})

const permittedPacks = (list, parent) => {
  if (parent.url != "modules") {
    return list.filter((item) => {
      const isServicePermitted = isServiceItemPermittedForUser(
        `services.${item.url}`
      )
      if (!isServicePermitted) return false
      return !item.reportName || isReportPermitted(item.reportName)
    })
  } else {
    // Modules/Packs
    return list.filter((item) => isPackPermitted(item.pack_id))
  }
}

const permittedModules = (list) => {
  return list.filter((item) => {
    return isModulePermitted(item.module_name)
  })
}

/**
 * Funnel the sidebar logout click through the shared auth composable so one logout path clears Vuex RBAC state, localStorage keys, and backend session state.
 *
 * @postcondition Delegates exactly one logout call to `useAuth()`.
 * @mutates Shared auth/session state through the composable only.
 */
const logoutFromSidebar = () => logout()
</script>
